let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { googleSignIn } from '../../utils/googleAuth';
import * as Apple from 'expo-apple-authentication';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CommonActions } from '@react-navigation/native';
import { useAuth } from '../../context/AuthContext';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const API_BASE_URL = 'https://api.jobeng.ai';


// ✅ Safe string normalizer
const s = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

export default function ProfessionalSignupScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    professionalEmail: '',
    currentPosition: '',
    yearsExperience: '',
    keySkills: '',
    password: '',
    confirmPassword: '',
  });

  const [isFocused, setIsFocused] = useState({
    firstName: false,
    lastName: false,
    professionalEmail: false,
    currentPosition: false,
    yearsExperience: false,
    keySkills: false,
    password: false,
    confirmPassword: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [appleAuthAvailable, setAppleAuthAvailable] = useState(false);




  React.useEffect(() => {
    checkAppleAuthAvailability();
  }, []);

  // ── Sync auth user data → ProfileScreen shared storage ──
  const syncAuthToProfile = async (user) => {
    try {
      const existing = await AsyncStorage.getItem(`professional_${_profUserId}_userProfile`);
      const profile = existing ? JSON.parse(existing) : {};
      const fullName = [user?.first_name || user?.firstName || formData.firstName || '', user?.last_name || user?.lastName || formData.lastName || ''].filter(Boolean).join(' ') || user?.name || '';
      profile.personal = {
        ...(profile.personal || {}),
        fullName: fullName || profile?.personal?.fullName || '',
        email: user?.email || formData.professionalEmail || profile?.personal?.email || '',
        phone: user?.phone || profile?.personal?.phone || '',
        jobTitle: formData.currentPosition || user?.job_title || profile?.personal?.jobTitle || '',
        company: user?.company || profile?.personal?.company || '',
        location: user?.location || profile?.personal?.location || '',
        profileImage: user?.profile_image || user?.avatar || profile?.personal?.profileImage || null,
        availability: 'Available',
      };
      await AsyncStorage.setItem(`professional_${_profUserId}_userProfile`, JSON.stringify(profile));
      const dash = { user: { fullName: profile.personal.fullName, email: profile.personal.email, jobTitle: profile.personal.jobTitle, profileImage: profile.personal.profileImage, availability: profile.personal.availability }, lastUpdated: new Date().toISOString() };
      await AsyncStorage.setItem('dashboardData', JSON.stringify(dash));
    } catch (e) {
      console.warn('syncAuthToProfile error:', e?.message);
    }
  };

    const resetToWelcome = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'WelcomeProfessionalScreen' }],
      })
    );
  };

  const checkAppleAuthAvailability = async () => {
    try {
      const isAvailable = await Apple.isAvailableAsync();
      setAppleAuthAvailable(isAvailable);
      console.log('✅ Apple Auth availability for professional signup:', isAvailable);
    } catch (error) {
      console.error('Error checking Apple auth availability:', error);
      setAppleAuthAvailable(false);
    }
  };

  const safeJson = async (response) => {
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return { message: text };
    }
  };

  // ✅ Persist token + user in BOTH keys (to support all screens)
  const persistAuth = async (user, token) => {
    try {
      if (token) {
        // Write to BOTH canonical token keys. 64 downstream screens read
        // 'userToken' while only 'auth_token' was being written previously,
        // which caused those screens to make unauthenticated requests after a
        // fresh install + signup. See audit report.
        await AsyncStorage.setItem('auth_token', token);
        await AsyncStorage.setItem('userToken', token);
        await AsyncStorage.setItem(`professional_${_profUserId}_token`, token);
      }
      if (user) {
        const u = typeof user === 'string' ? user : JSON.stringify(user);
        await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, u);
        await AsyncStorage.setItem(`professional_${_profUserId}_user`, u);

        // Canonical identity keys — required for documents upload, profile sync, etc.
        await AsyncStorage.setItem('userData', u);
        await AsyncStorage.setItem('user', u);

        const userObj = typeof user === 'string' ? JSON.parse(user) : user;
        const uid = userObj?.id || userObj?.user_id || userObj?.userId;
        if (uid != null) {
          const uidStr = String(uid);
          await AsyncStorage.setItem('user_id', uidStr);
          await AsyncStorage.setItem('userId', uidStr);
          await AsyncStorage.setItem('professional_user_id', uidStr);
        }
        if (userObj?.email) {
          await AsyncStorage.setItem('userEmail', String(userObj.email));
        }
      }
      if (login && user && token) {
        await login(user, token);
      }
    } catch (e) {
      console.error('❌ persistAuth error:', e);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: String(value ?? ''),
    }));
  };

  const handleFocus = (field) => setIsFocused((prev) => ({ ...prev, [field]: true }));
  const handleBlur = (field) => setIsFocused((prev) => ({ ...prev, [field]: false }));

  // ---------------------------
  // OPTIONAL: init profile in DB (safe if endpoint missing)
  // ---------------------------
  const initProfessionalProfileInDB = async (token, payload) => {
    try {
      if (!token) return;

      // If your backend has a dedicated endpoint, keep it here.
      // If not, it will fail silently and app continues.
      const res = await fetch(`${API_BASE_URL}/api/profile/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          profile_type: 'professional',
          ...payload,
        }),
      });

      const data = await safeJson(res);
      if (!res.ok) {
        console.warn('⚠️ profile init not available:', data?.message || res.status);
      }
    } catch (e) {
      console.warn('⚠️ initProfessionalProfileInDB failed (ignored):', e?.message || e);
    }
  };

  // ---------------------------
  // ✅ OPTIONAL: “test ATS” baseline init (safe if endpoint missing)
  // ---------------------------
  const initAtsBaseline = async (token, payload) => {
    try {
      if (!token) return;

      // Try a couple of common routes (first one that works)
      const candidates = ['/api/ats/init', '/api/ats/baseline', '/api/ats/create'];

      for (const ep of candidates) {
        try {
          const res = await fetch(`${API_BASE_URL}${ep}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              type: 'test_ats',
              source: 'signup',
              profile_type: 'professional',
              ...payload,
            }),
          });

          const data = await safeJson(res);
          if (res.ok && (data?.success === true || data?.status === 'ok')) {
            console.log('✅ ATS baseline created via:', ep);
            return;
          }
        } catch {
          // try next endpoint
        }
      }

      console.warn('⚠️ ATS baseline endpoint not found (ignored).');
    } catch (e) {
      console.warn('⚠️ initAtsBaseline failed (ignored):', e?.message || e);
    }
  };

  const validateForm = () => {
    const firstName = s(formData.firstName).trim();
    const lastName = s(formData.lastName).trim();
    const email = s(formData.professionalEmail).trim().toLowerCase();
    const currentPosition = s(formData.currentPosition).trim();
    const yearsExperience = s(formData.yearsExperience).trim();
    const keySkills = s(formData.keySkills).trim();
    const password = s(formData.password);
    const confirmPassword = s(formData.confirmPassword);

    if (!firstName) return Alert.alert('Validation Error', 'Please enter your first name'), false;
    if (!lastName) return Alert.alert('Validation Error', 'Please enter your last name'), false;

    if (!email) return Alert.alert('Validation Error', 'Please enter your professional email'), false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return Alert.alert('Validation Error', 'Please enter a valid email address'), false;

    if (!currentPosition) return Alert.alert('Validation Error', 'Please enter your current position'), false;

    if (!yearsExperience) return Alert.alert('Validation Error', 'Please enter years of experience'), false;
    const years = parseInt(yearsExperience, 10);
    if (Number.isNaN(years) || years < 0) return Alert.alert('Validation Error', 'Years of experience must be a number'), false;

    if (!keySkills) return Alert.alert('Validation Error', 'Please enter your key skills'), false;

    if (!password.trim()) return Alert.alert('Validation Error', 'Please enter a password'), false;
    if (password.length < 6) return Alert.alert('Validation Error', 'Password must be at least 6 characters long'), false;

    if (password !== confirmPassword) return Alert.alert('Validation Error', 'Passwords do not match'), false;

    if (!agreeToTerms) return Alert.alert('Validation Error', 'Please agree to the Terms of Service and Privacy Policy'), false;

    return true;
  };

  // ---------------------------
  // Email Signup (DB linked + ATS baseline)
  // ---------------------------
  const handleSignup = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const fullName = `${s(formData.firstName).trim()} ${s(formData.lastName).trim()}`.trim();
      const email = s(formData.professionalEmail).trim().toLowerCase();
      const years = parseInt(s(formData.yearsExperience).trim(), 10);

      const signupData = {
        full_name: fullName,
        email,
        password: s(formData.password).trim(),
        user_type: 'professional',
        profile_type: 'professional',
        first_name: s(formData.firstName).trim(),
        last_name: s(formData.lastName).trim(),
        current_position: s(formData.currentPosition).trim(),
        years_experience: years,
        key_skills: s(formData.keySkills).trim(),
      };

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(signupData),
      });

      const data = await safeJson(response);

      if (!response.ok || !data?.success) {
        const msg =
          data?.error ||
          data?.message ||
          (response.status === 409 ? 'An account with this email already exists.' : 'Signup failed. Please try again.');
        Alert.alert('Signup Error', msg);
        return;
      }

      const userData = data?.data?.user || data?.user;
      const token = data?.data?.token || data?.token;

      if (!token || !userData) {
        Alert.alert('Signup Error', 'Missing token/user from server response.');
        return;
      }

      userData.user_type = 'professional';
      userData.profile_type = 'professional';

      await persistAuth(userData, token);

      await AsyncStorage.setItem(
        'professional_profile',
        JSON.stringify({
          current_position: s(formData.currentPosition).trim(),
          years_experience: years,
          key_skills: s(formData.keySkills).trim(),
          signup_date: new Date().toISOString(),
          signup_method: 'email',
        })
      );

      // ✅ DB init
      await initProfessionalProfileInDB(token, {
        current_position: s(formData.currentPosition).trim(),
        years_experience: years,
        key_skills: s(formData.keySkills).trim(),
      });

      // ✅ ATS baseline “test ATS”
      await initAtsBaseline(token, {
        full_name: fullName,
        email,
        current_position: s(formData.currentPosition).trim(),
        years_experience: years,
        key_skills: s(formData.keySkills).trim(),
      });

      Alert.alert(
        'Welcome to JobEng Professional!',
        'Your professional account has been created successfully.',
        [{ text: 'Continue', onPress: resetToWelcome }]
      );
    } catch (error) {
      console.error('Professional signup error:', error);
      const msg =
        String(error?.message || '').includes('Network request failed')
          ? 'Network error. Please check your internet connection.'
          : error?.message || 'Signup failed. Please try again.';
      Alert.alert('Signup Error', msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------
  // Google Signup
  // ---------------------------
  const handleGoogleSignup = async () => {
    try {
      setIsLoading(true);
      // Native Google Sign-In (returns { accessToken, idToken })
      const authentication = await googleSignIn();
      await handleGoogleAuthSuccess(authentication);
    } catch (e) {
      if (e?.cancelled) return;
      console.error('Google signup error:', e);
      Alert.alert('Google Signup Error', 'Unable to sign up with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuthSuccess = async (auth) => {
    setIsLoading(true);
    try {
      if (!auth?.accessToken) throw new Error('Invalid Google authentication result');

      // optional google user info
      let userInfo = null;
      try {
        const r = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${auth.accessToken}`);
        userInfo = await r.json();
      } catch {}

      const response = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_token: auth.accessToken,
          id_token: auth.idToken,
          user_info: userInfo,
          user_type: 'professional',
          is_signup: true,
        }),
      });

      const data = await safeJson(response);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Google signup failed');
      }

      const token = data?.data?.token;
      const user = data?.data?.user;

      if (!token || !user) throw new Error('Missing token/user from Google signup response');

      user.user_type = 'professional';
      user.profile_type = 'professional';

      await persistAuth(user, token);

      // ✅ ATS baseline “test ATS”
      await initAtsBaseline(token, {
        full_name: user?.full_name || user?.name || 'Google Professional User',
        email: user?.email || userInfo?.email || '',
        current_position: 'Not specified',
        years_experience: 0,
        key_skills: 'General engineering',
      });

      Alert.alert(
        'Welcome to JobEng Professional!',
        'Your account has been created with Google.',
        [{ text: 'Continue', onPress: resetToWelcome }]
      );
    } catch (e) {
      console.error('❌ Google signup error:', e);
      Alert.alert('Google Signup Error', e?.message || 'Failed to complete Google signup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------
  // Apple Signup
  // ---------------------------
  const handleAppleSignup = async () => {
    if (!appleAuthAvailable) {
      Alert.alert('Apple Signup', 'Apple Sign In is not available on this device');
      return;
    }

    setIsLoading(true);
    try {
      const credential = await Apple.signInAsync({
        requestedScopes: [Apple.AppleAuthenticationScope.FULL_NAME, Apple.AppleAuthenticationScope.EMAIL],
      });

      if (!credential?.identityToken) throw new Error('Missing Apple identity token');

      const response = await fetch(`${API_BASE_URL}/api/auth/apple-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          identity_token: credential.identityToken,
          authorization_code: credential.authorizationCode,
          user_type: 'professional',
          is_signup: true,
          email: credential.email,
          full_name: credential.fullName
            ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
            : null,
          user_info: {
            id: credential.user,
            email: credential.email,
            name: credential.fullName
              ? `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim()
              : 'Apple Professional User',
            firstName: credential.fullName?.givenName,
            lastName: credential.fullName?.familyName,
          },
        }),
      });

      const result = await safeJson(response);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Apple signup failed');
      }

      const token = result?.data?.token;
      const user = result?.data?.user;

      if (!token || !user) throw new Error('Missing token/user from Apple signup response');

      user.user_type = 'professional';
      user.profile_type = 'professional';

      await persistAuth(user, token);

      await initProfessionalProfileInDB(token, {
        current_position: 'Not specified',
        years_experience: 0,
        key_skills: 'General engineering',
      });

      await initAtsBaseline(token, {
        full_name: user?.full_name || 'Apple Professional User',
        email: user?.email || credential.email || '',
        current_position: 'Not specified',
        years_experience: 0,
        key_skills: 'General engineering',
      });

      Alert.alert(
        'Welcome to JobEng Professional!',
        'Your account has been created with Apple.',
        [{ text: 'Continue', onPress: resetToWelcome }]
      );
    } catch (error) {
      console.error('❌ Apple signup error:', error);
      if (error?.code !== 'ERR_CANCELED') {
        Alert.alert('Apple Signup Error', error?.message || 'Failed to complete Apple signup. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------
  // UI
  // ---------------------------
  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.keyboardAvoid}>
        <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
          <View style={styles.container}>
            <View style={styles.headerContainer}>
              <Image source={require('../../../assets/jobeng_full_logo.png')} style={styles.logo} resizeMode="contain" />
              <Text style={styles.subtitle}>UK Engineering Excellence</Text>
            </View>

            <View style={styles.formContainer}>
              <Text style={styles.title}>Join Professional Network</Text>
              <Text style={styles.description}>
                Connect with engineering professionals and advance your career
              </Text>



              {/* First Name */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.firstName && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="person-outline"
                    size={20}
                    color={isFocused.firstName ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="First Name"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.firstName ?? '')}
                    onChangeText={(v) => handleInputChange('firstName', v)}
                    onFocus={() => handleFocus('firstName')}
                    onBlur={() => handleBlur('firstName')}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Last Name */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.lastName && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="person-outline"
                    size={20}
                    color={isFocused.lastName ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Last Name"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.lastName ?? '')}
                    onChangeText={(v) => handleInputChange('lastName', v)}
                    onFocus={() => handleFocus('lastName')}
                    onBlur={() => handleBlur('lastName')}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Professional Email */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.professionalEmail && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="mail-outline"
                    size={20}
                    color={isFocused.professionalEmail ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Professional Email"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.professionalEmail ?? '')}
                    onChangeText={(v) => handleInputChange('professionalEmail', v)}
                    onFocus={() => handleFocus('professionalEmail')}
                    onBlur={() => handleBlur('professionalEmail')}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Current Position */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.currentPosition && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="briefcase-outline"
                    size={20}
                    color={isFocused.currentPosition ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Current Position"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.currentPosition ?? '')}
                    onChangeText={(v) => handleInputChange('currentPosition', v)}
                    onFocus={() => handleFocus('currentPosition')}
                    onBlur={() => handleBlur('currentPosition')}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Years Experience */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.yearsExperience && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="time-outline"
                    size={20}
                    color={isFocused.yearsExperience ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Years of Experience"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.yearsExperience ?? '')}
                    onChangeText={(v) => handleInputChange('yearsExperience', v)}
                    onFocus={() => handleFocus('yearsExperience')}
                    onBlur={() => handleBlur('yearsExperience')}
                    keyboardType="numeric"
                  />
                </View>
              </View>

              {/* Key Skills */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.keySkills && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="construct-outline"
                    size={20}
                    color={isFocused.keySkills ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Key Skills (e.g. AutoCAD, HVAC, PLC)"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.keySkills ?? '')}
                    onChangeText={(v) => handleInputChange('keySkills', v)}
                    onFocus={() => handleFocus('keySkills')}
                    onBlur={() => handleBlur('keySkills')}
                    autoCapitalize="sentences"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Password */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.password && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={isFocused.password ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Password"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.password ?? '')}
                    onChangeText={(v) => handleInputChange('password', v)}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity onPress={() => setShowPassword((p) => !p)}>
                    <Ionicons name={showPassword ? 'eye-off' : 'eye'} size={20} color="#8E8E93" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Confirm Password */}
              <View style={styles.inputContainer}>
                <View style={[styles.inputWrapper, isFocused.confirmPassword && styles.inputWrapperFocused]}>
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={isFocused.confirmPassword ? '#007AFF' : '#8E8E93'}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Confirm Password"
                    placeholderTextColor="#8E8E93"
                    value={String(formData.confirmPassword ?? '')}
                    onChangeText={(v) => handleInputChange('confirmPassword', v)}
                    onFocus={() => handleFocus('confirmPassword')}
                    onBlur={() => handleBlur('confirmPassword')}
                    secureTextEntry={!showConfirmPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity onPress={() => setShowConfirmPassword((p) => !p)}>
                    <Ionicons name={showConfirmPassword ? 'eye-off' : 'eye'} size={20} color="#8E8E93" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Terms */}
              <TouchableOpacity style={styles.termsRow} onPress={() => setAgreeToTerms((p) => !p)}>
                <Ionicons name={agreeToTerms ? 'checkbox' : 'square-outline'} size={20} color="#007AFF" />
                <Text style={styles.termsText}>
                  I agree to the <Text style={styles.linkText}>Terms</Text> and <Text style={styles.linkText}>Privacy Policy</Text>
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.signupButton, isLoading && styles.signupButtonDisabled]}
                onPress={handleSignup}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.signupButtonText}>Create Professional Account</Text>
                    <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
                  </>
                )}
              </TouchableOpacity>

              {/* ✅ Forgot password link — gracefully degrades if ForgotPassword
                  screen is not registered in the navigator. */}
              <TouchableOpacity
                onPress={() => {
                  try {
                    const state = navigation.getState && navigation.getState();
                    const allRoutes = [];
                    const collect = (s) => {
                      if (!s || !s.routeNames) return;
                      s.routeNames.forEach(n => allRoutes.push(n));
                      (s.routes || []).forEach(r => r.state && collect(r.state));
                    };
                    collect(state);
                    if (allRoutes.includes('ForgotPassword')) {
                      navigation.navigate('ForgotPassword');
                      return;
                    }
                  } catch {}
                  Alert.alert(
                    'Reset your password',
                    'Email support@jobeng.ai from the address you signed up with and our team will reset your password for you.',
                    [{ text: 'OK' }],
                  );
                }}
                style={{ marginTop: 12, alignSelf: 'center' }}
              >
                <Text style={{ color: '#007AFF', fontWeight: '600' }}>Forgot password?</Text>
              </TouchableOpacity>

              <View style={styles.footerContainer}>
                <Text style={styles.footerText}>
                  Already have an account?{' '}
                  <Text style={styles.footerLink} onPress={() => navigation.navigate('ProfessionalLogin')}>
                    Sign In
                  </Text>
                </Text>

              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// Styles (kept compatible with yours)
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F2F2F7' },
  keyboardAvoid: { flex: 1 },
  scrollContainer: { flexGrow: 1 },
  container: { flex: 1, paddingHorizontal: 20, paddingTop: 40 },
  headerContainer: { alignItems: 'center', marginBottom: 40 },
  logo: { width: 120, height: 60, marginBottom: 10 },
  subtitle: { fontSize: 16, color: '#8E8E93', fontWeight: '500' },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  title: { fontSize: 24, fontWeight: 'bold', color: '#1C1C1E', textAlign: 'center', marginBottom: 8 },
  description: { fontSize: 16, color: '#8E8E93', textAlign: 'center', marginBottom: 24 },
  socialSignupContainer: { marginBottom: 24 },
  socialButton: {
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  socialButtonText: { fontSize: 16, fontWeight: '500', color: '#1C1C1E', marginLeft: 8 },
  divider: { flexDirection: 'row', alignItems: 'center', marginBottom: 24 },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#E5E5EA' },
  dividerText: { marginHorizontal: 16, fontSize: 14, color: '#8E8E93' },

  inputContainer: { marginBottom: 16 },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    paddingHorizontal: 16,
    height: 50,
  },
  inputWrapperFocused: { borderColor: '#007AFF', backgroundColor: '#FFFFFF' },
  inputIcon: { marginRight: 12 },
  input: { flex: 1, fontSize: 16, color: '#1C1C1E' },

  termsRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4, marginBottom: 18 },
  termsText: { marginLeft: 8, color: '#1C1C1E', fontSize: 13 },
  linkText: { color: '#007AFF', fontWeight: '700' },

  signupButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  signupButtonDisabled: { opacity: 0.7 },
  signupButtonText: { fontSize: 16, fontWeight: '600', color: '#FFFFFF', marginRight: 8 },

  footerContainer: { alignItems: 'center', marginTop: 16 },
  footerText: { fontSize: 14, color: '#8E8E93', marginBottom: 16 },
  footerLink: { color: '#007AFF', fontWeight: '500' },

  switchModeButton: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  switchModeText: { fontSize: 14, color: '#007AFF', marginLeft: 4 },
});
